<template>
  <v-app>
    <v-content class="gp-page">
      <v-container fluid class="fill-height d-flex justify-center align-start pa-4 pa-sm-6">
        <v-card class="gp-card" elevation="0" rounded="lg">

          <!-- Loading state -->
          <div v-if="loading" class="text-center py-6" role="status" aria-live="polite">
            <v-progress-circular
                indeterminate
                color="primary"
                :size="72"
                :width="5"
                class="mb-6"
            ></v-progress-circular>
            <div class="gp-eyebrow mb-1">Please wait</div>
            <h1 class="gp-title mb-0">Checking payment&hellip;</h1>
            <p class="gp-subtext mt-2 mb-0">This will only take a moment.</p>
          </div>

          <!-- Result state -->
          <div v-else class="text-center">
            <div :class="['gp-status-badge', paymentSuccess ? 'gp-status-badge--success' : 'gp-status-badge--warning']"
                 aria-hidden="true">
              <img :src="statusImg" alt="" class="gp-status-img"/>
            </div>

            <template v-if="paymentSuccess">
              <h1 class="gp-title gp-title--success mt-6 mb-2">Payment Successful</h1>
              <p class="gp-subtext mb-6">
                Your amount<span v-if="$route.query.ebpNo"> for voucher number
                <strong>{{ $route.query.ebpNo }}</strong></span> has been paid and the application has been received.
              </p>
              <v-alert :value="true" class="hide-on-print gp-alert gp-alert--success text-left" type="success"
                       border="left" colored-border icon="mdi-check-circle" role="status">
                Your amount has been paid and the application has been received.<br>
                For further inquiry, please email <a href="mailto:info@immigration.gov.np">info@immigration.gov.np</a>
                or call <a href="tel:+97714429659">+977-01-4429659</a>.
              </v-alert>
            </template>

            <template v-else>
              <h1 class="gp-title gp-title--warning mt-6 mb-2">Acknowledgement Pending</h1>
              <p class="gp-subtext mb-6">
                Your amount<span v-if="$route.query.ebpNo"> for voucher number
                <strong>{{ $route.query.ebpNo }}</strong></span> has been received.
              </p>
              <v-alert :value="true" class="hide-on-print gp-alert gp-alert--warning text-left" type="warning"
                       border="left" colored-border icon="mdi-clock-alert" role="status">
                Sometimes it takes about 5&ndash;30 minutes to register the successful voucher.<br>
                For further inquiry, please email <a href="mailto:info@immigration.gov.np">info@immigration.gov.np</a>
                or call <a href="tel:+97714429659">+977-01-4429659</a>.
              </v-alert>
            </template>

            <v-btn
                color="primary"
                x-large
                block
                depressed
                class="gp-btn hide-on-print mt-7"
                @click="moveToCheckPayment"
            >
              Check Payment
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>

          <v-divider class="gp-divider hide-on-print"></v-divider>

          <div class="d-flex flex-wrap align-center justify-center gp-footer hide-on-print">
            <img :src="logoUrl" alt="Nepal Immigration" class="gp-footer-logo mr-6">
            <img :src="sarkarLogo" alt="Government of Nepal" class="gp-footer-sarkar">
          </div>
        </v-card>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import {API_URL, VERSION} from "../../config/config";
import Http from 'axios';
import {NEPAL_SARKAR_LOGO, NEPALIPORT_LOGO_URL} from "@/constant/appConstant";

export default {
  name: "GovPaySuccess",
  data() {
    return {
      sarkarLogo: NEPAL_SARKAR_LOGO,
      logoUrl: NEPALIPORT_LOGO_URL,
      printContent: {},
      statusImg: require('@/assets/successful.png'),
      paymentSuccess: false,
      loading: true,
    }
  },
  beforeRouteEnter(to, from, next) {
    if (from.name && (from.name === 'OnArrival' || from.name === 'OnlineApplication' || from.name === 'OnArrival')) {
      next(vm => {
        vm.$router.push({
          name: 'OnlineApplication'
        })
      })
    }
    next();
  },
  methods: {
    moveToApplyAgain() {
      let routeData = this.$router.resolve({name: 'HomePage'});
      window.open(routeData.href, '_self');
    },
    moveToCheckPayment(){
      window.location.href = "https://nepaliport-new.immigration.gov.np/card-payment";
    },
    checkPaymentStatus() {
      const {ebpNo, hashValue} = this.$route.query

      Http.post(`${API_URL}${VERSION}/public-check-payment-status/`, {
        update: true,
        voucher_no: ebpNo,
        hashValue: hashValue
      }).then((res) => {
        this.printContent = res.data;
        this.paymentSuccess = res.data.status
      }).catch(err => {
        this.paymentSuccess = false;
      }).finally(() => {
        this.loading = false;
      })
    }
  },
  mounted() {
    this.checkPaymentStatus();
  }
}
</script>


<style scoped>
/* ---- local design tokens ---- */
.gp-page {
  --gp-primary: #0B5FBF;
  --gp-success: #1B8A5A;
  --gp-success-bg: #EAF7F0;
  --gp-warning: #B5650C;
  --gp-warning-bg: #FEF3E6;
  --gp-text: rgba(0, 0, 0, 0.87);
  --gp-text-muted: rgba(0, 0, 0, 0.60);
  --gp-border: rgba(0, 0, 0, 0.09);
  --gp-radius-lg: 16px;
  --gp-radius-md: 10px;
  --gp-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 8px 24px rgba(16, 24, 40, 0.08);

  background: #F4F6F9;
  min-height: 100vh;
}

.gp-card {
  width: 100%;
  max-width: 680px;
  margin-top: 6vh;
  padding: 64px 56px 44px;
  background: #FFFFFF;
  border: 1px solid var(--gp-border);
  border-radius: var(--gp-radius-lg) !important;
  box-shadow: var(--gp-shadow) !important;
}

/* ---- typography ---- */
.gp-eyebrow {
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gp-primary);
}

.gp-title {
  font-size: 2rem;
  line-height: 1.25;
  font-weight: 700;
  color: var(--gp-text);
}

.gp-title--success {
  color: var(--gp-success);
}

.gp-title--warning {
  color: var(--gp-warning);
}

.gp-subtext {
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--gp-text-muted);
}

/* ---- status badge ---- */
.gp-status-badge {
  width: 128px;
  height: 128px;
  margin: 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gp-status-badge--success {
  background: var(--gp-success-bg);
}

.gp-status-badge--warning {
  background: var(--gp-warning-bg);
}

.gp-status-img {
  width: 76px;
  height: 76px;
  object-fit: contain;
}

/* ---- alert ---- */
.gp-alert {
  font-size: 1rem;
  line-height: 1.65;
  border-radius: var(--gp-radius-md) !important;
}

.gp-alert a {
  font-weight: 600;
  text-decoration: underline;
  color: inherit;
}

.gp-alert--success {
  color: #1E2A24 !important;
  background: var(--gp-success-bg) !important;
  caret-color: var(--gp-success) !important;
}

.gp-alert--warning {
  color: #2B2013 !important;
  background: var(--gp-warning-bg) !important;
  caret-color: var(--gp-warning) !important;
}

/* ---- button ---- */
.gp-btn {
  height: 56px !important;
  border-radius: var(--gp-radius-md) !important;
  font-weight: 600;
  font-size: 1.125rem;
  letter-spacing: 0.01em;
  text-transform: none !important;
  box-shadow: none !important;
  transition: filter 0.15s ease, transform 0.05s ease;
}

.gp-btn:hover {
  filter: brightness(0.94);
}

.gp-btn:active {
  transform: translateY(1px);
}

.gp-btn:focus-visible {
  outline: 2px solid var(--gp-primary);
  outline-offset: 2px;
}

/* ---- footer ---- */
.gp-divider {
  margin: 40px 0 28px;
}

.gp-footer-logo {
  width: 210px;
  max-width: 48%;
  height: auto;
}

.gp-footer-sarkar {
  width: 68px;
  max-width: 22%;
  height: auto;
}

@media print {
  .hide-on-print {
    display: none !important;
  }
}

/* Tablet */
@media (min-width: 600px) and (max-width: 959px) {
  .gp-card {
    max-width: 600px;
    margin-top: 5vh;
    padding: 52px 44px 36px;
  }

  .gp-title {
    font-size: 1.75rem;
  }

  .gp-status-badge {
    width: 112px;
    height: 112px;
  }

  .gp-status-img {
    width: 66px;
    height: 66px;
  }
}

/* Mobile */
@media (max-width: 599px) {
  .gp-card {
    max-width: 100%;
    margin-top: 0;
    padding: 36px 24px 28px;
    border-radius: 12px !important;
  }

  .gp-eyebrow {
    font-size: 0.75rem;
  }

  .gp-title {
    font-size: 1.5rem;
  }

  .gp-subtext {
    font-size: 1rem;
  }

  .gp-status-badge {
    width: 96px;
    height: 96px;
  }

  .gp-status-img {
    width: 56px;
    height: 56px;
  }

  .gp-btn {
    height: 52px !important;
    font-size: 1.0625rem;
  }
}

/* Small mobile */
@media (max-width: 360px) {
  .gp-card {
    padding: 28px 18px 22px;
  }

  .gp-title {
    font-size: 1.3rem;
  }

  .gp-subtext {
    font-size: 0.9375rem;
  }

  .gp-status-badge {
    width: 84px;
    height: 84px;
  }

  .gp-status-img {
    width: 48px;
    height: 48px;
  }
}
</style>
